import { StackSidePanelContext } from "$root/components/common/Stack/StackSidePanel";
import { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import { chartsStale, generateColumnColor } from "$root/functional-areas/portfolio-lab/common";
import { useSeedMetrics } from "$root/functional-areas/portfolio-lab/hooks/useEnsureMetricsSeries";
import { useLabSession } from "$root/functional-areas/portfolio-lab/hooks/useLabSession";
import { portfolioLabRepository } from "$root/functional-areas/portfolio-lab/repository";
import { workspaceRepository } from "$root/functional-areas/workspace/repository";
import { useLocaleFormatters } from "$root/localization/hooks";
import {
	PortfolioQueryWidgetBase,
	type PortfolioWidgetData,
	WidgetStatus,
} from "$root/pages/PortfolioDetails/PortfolioWidgetStatus";
import { AgGridWithModules } from "$root/third-party-integrations/ag-grid/ag-grid";
import { useMappedDynamicQueries } from "$root/third-party-integrations/react-query/hooks";
import { agExtra } from "$root/ui-lib/table/default-configuration";
import { seconds } from "$root/utils";
import { Card } from "$root/widgets-architecture/layout/Card";
import type { ConceptDefinitionDTO, DataResponseDTO, ParameterizedConceptDTO } from "@eu.mdotm.api/smart-workspaces";
import { IconButton } from "@mdotm/shadcn/mdotm/components";
import { Row } from "@mdotm/shadcn/legacy/components";
import { useDeepEqualMemo } from "@mdotm/shadcn/legacy/react-extensions";
import { atom, getDefaultStore, type RwAtom, useAtomValue } from "@mdotm/shadcn/legacy/reactivity";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { clamp, instanceEqualsOrFastDeepEqual, maybeCall, type SetterOrUpdaterParam } from "@mdotm/shadcn/legacy/utils";
import { useMandatoryContext } from "@mdotm/utils/react-extra";
import { useQueries } from "@tanstack/react-query";
import type { ColDef, ColumnState } from "ag-grid-community";
import debounce from "debounce";
import { useEffect, useMemo, useState } from "react";
import { type GetPortfolioLabChartItem, type PortfolioLabChartItem, PortfolioLabContext } from "../../../ctx";
import {
	buildConceptMetricColumnDefs,
	type ConceptMetricsRow,
	extractConceptMetricValues,
	NAME_COL_ID,
} from "../concept-metrics";
import { MetricsBlurOverlay } from "../MetricsBlurOverlay";
import { ShowDeltaToggle } from "../shared/ShowDeltaToggle";
import { PortfolioMetricsSettings } from "./PortfolioMetricsSettings";

type PortfolioMetricsData = {
	allMetricTypes: string[];
	rowData: ConceptMetricsRow[];
};

export type PortfolioMetricsProps = {
	chartConfig: GetPortfolioLabChartItem<"TABLE">;
};

type PortfolioMetricsInnerProps = {
	allMetricTypes: string[];
	rowData: ConceptMetricsRow[];
	columnStates: ColumnState[];
	columns$: RwAtom<ColumnState[] | null>;
	investmentsCount: number;
	conceptDefinitions: ConceptDefinitionDTO[];
	parametrizedConcepts: ParameterizedConceptDTO[];
	showDelta: boolean;
};

function PortfolioMetricsInner({
	allMetricTypes,
	rowData,
	columnStates,
	columns$,
	investmentsCount,
	conceptDefinitions,
	parametrizedConcepts,
	showDelta,
}: PortfolioMetricsInnerProps) {
	const { formatNumber } = useLocaleFormatters();

	const columnsPersistence = agExtra.useSyncColStateWithAtom(columns$);

	const orderedMetrics = useMemo(
		() => (columnStates.length > 0 ? columnStates.map((c) => c.colId) : allMetricTypes),
		[allMetricTypes, columnStates],
	);

	const conceptDefsById = useMemo(() => new Map(conceptDefinitions.map((d) => [d.id!, d])), [conceptDefinitions]);

	const columnDefs: ColDef<ConceptMetricsRow>[] = useDeepEqualMemo(
		() =>
			buildConceptMetricColumnDefs({
				orderedMetricUuids: orderedMetrics,
				parametrizedConcepts,
				conceptDefsById,
				columnStates,
				formatNumber,
				showDelta,
				valueQualifier: "PortfolioLab/Metrics/Value",
			}),
		[orderedMetrics, columnStates, formatNumber, parametrizedConcepts, conceptDefsById, showDelta],
	);

	const height = 50 + 48 * clamp(investmentsCount, 1, 6);
	return (
		<div style={{ height }}>
			<AgGridWithModules<ConceptMetricsRow>
				rowData={rowData}
				columnDefs={columnDefs}
				suppressMovableColumns={false}
				suppressHorizontalScroll={false}
				onGridReady={columnsPersistence.onGridReady}
				onStateUpdated={columnsPersistence.onStateUpdated}
			/>
		</div>
	);
}

export function PortfolioMetrics({ chartConfig }: PortfolioMetricsProps): React.JSX.Element {
	const sidePanelCtx = useMandatoryContext(StackSidePanelContext);
	const ctx = useMandatoryContext(PortfolioLabContext);
	const investments = useAtomValue(ctx.investments$);
	const { saveLabSession } = useLabSession();

	useSeedMetrics(chartConfig);

	const columnStates: ColumnState[] = chartConfig.preference.columns;
	const showDelta = chartConfig.preference.showDelta;
	const chartUuid = chartConfig.uuid;

	const [columns$] = useState<RwAtom<ColumnState[] | null>>(() => {
		const findTableChart = (charts: PortfolioLabChartItem[]) =>
			charts.find((c): c is GetPortfolioLabChartItem<"TABLE"> => c.type === "TABLE" && c.uuid === chartUuid);
		return atom(
			(get): ColumnState[] | null => findTableChart(get(ctx.charts$))?.preference.columns ?? null,
			(get, set, next: SetterOrUpdaterParam<ColumnState[] | null>) => {
				const charts: PortfolioLabChartItem[] = get(ctx.charts$);
				const current = findTableChart(charts)?.preference.columns ?? null;
				const value = maybeCall(next, current);
				// Keep only concept columns; drop the pinned "name" column so it never leaks into the persisted `series`.
				const conceptColumns = value?.filter((col) => col.colId !== NAME_COL_ID) ?? null;
				if (instanceEqualsOrFastDeepEqual(current, conceptColumns)) {
					return;
				}
				set(
					ctx.charts$,
					charts.map((c) =>
						c.type === "TABLE" && c.uuid === chartUuid
							? { ...c, preference: { ...c.preference, columns: conceptColumns ?? [] } }
							: c,
					),
				);
			},
		);
	});

	const saveLabSessionMemo = useMemo(() => debounce(saveLabSession, seconds(1)), [saveLabSession]);

	useEffect(() => {
		const unSubscribe = getDefaultStore().sub(columns$, () => {
			saveLabSessionMemo();
		});
		return unSubscribe;
	}, [columns$, saveLabSessionMemo]);
	const parametrizedConceptsBasket = useAtomValue(ctx.parametrizedConcepts$);

	// Resolve THIS chart's `series` (data dimension) → parametrized concepts from the session basket.
	// Driven by series, not the column UI state, so reorder/hide/resize never re-triggers the metrics queries.
	const parametrizedConcepts = useMemo(
		() =>
			chartConfig.series
				.map((uuid) => parametrizedConceptsBasket.find((pc) => pc.uuid === uuid))
				.filter((pc): pc is ParameterizedConceptDTO => pc != null),
		[chartConfig.series, parametrizedConceptsBasket],
	);

	// Concept definitions come from the catalog; each parametrized concept resolves its definition by conceptId.
	const conceptDefinitionsQuery = workspaceRepository.getConceptDefinitions.useQuery("VIRTUAL_INVESTMENT");
	const investmentQueries = useDeepEqualMemo(
		() =>
			investments.map((inv) => ({
				...portfolioLabRepository.portfolioMetrics.getQueryConfig(inv.virtualUuid, parametrizedConcepts),
				enabled: parametrizedConcepts.length > 0,
				keepPreviousData: true,
			})),
		[investments, parametrizedConcepts],
	);
	const investmentsMetricsQueryRaw = useQueries({ queries: investmentQueries });

	// Baseline (pre-edit) metrics power the in-cell deltas. Deliberately NOT merged into the mapped
	// widget query: a missing/failed baseline must never block or error the grid — cells just render
	// without the delta. Also deliberately NOT gated on `showDelta`: with the repository's
	// staleTime 0, re-enabling would refetch and can replace the on-screen baseline mid-session —
	// the toggle must stay purely presentational.
	const baselineQueries = useDeepEqualMemo(
		() =>
			investments.map((inv) => ({
				...portfolioLabRepository.portfolioMetricsBaseline.getQueryConfig(inv.virtualUuid, parametrizedConcepts),
				enabled: parametrizedConcepts.length > 0,
				keepPreviousData: true,
			})),
		[investments, parametrizedConcepts],
	);
	const baselineMetricsQueryRaw = useQueries({ queries: baselineQueries });
	const baselineMetricValues = baselineMetricsQueryRaw.map((result) =>
		result.data != null ? extractConceptMetricValues(result.data, parametrizedConcepts) : undefined,
	);

	const investmentsMetricsQuery = useMappedDynamicQueries(
		(results: DataResponseDTO[]): PortfolioWidgetData<PortfolioMetricsData> => {
			const allMetricTypes = parametrizedConcepts.map((c) => c.uuid!).filter(Boolean);
			const rowData: ConceptMetricsRow[] = investments.map((inv, i) => ({
				// Keyed by virtualUuid, not the source uuid: the same investment can be loaded twice
				// (two lab columns, same source), and source-uuid keys would collapse them into one row.
				uuid: inv.virtualUuid,
				name: inv.name,
				color: generateColumnColor(i),
				metricValues: extractConceptMetricValues(results[i], parametrizedConcepts),
				baselineMetricValues: baselineMetricValues[i],
			}));
			if (rowData.length === 0) {
				return { widgetStatus: WidgetStatus.EMPTY, data: { allMetricTypes: [], rowData: [] } };
			}
			return { widgetStatus: WidgetStatus.READY, data: { allMetricTypes, rowData } };
		},
		[investments, parametrizedConcepts, baselineMetricValues],
		investmentsMetricsQueryRaw,
	);

	return (
		<MetricsBlurOverlay qualifier="PortfolioLab/Chart(TABLE)" enableBlur={investments.some(chartsStale)}>
			<Card
				className="min-h-[200px]"
				title="Portfolio metrics"
				actionHeader={
					<Row alignItems="center" gap={16}>
						<ShowDeltaToggle chart={chartConfig} />
						<IconButton
							icon="Settings"
							size={20}
							color={themeCSSVars.palette_N500}
							onClick={() => sidePanelCtx.push(() => <PortfolioMetricsSettings chartUuid={chartUuid} />)}
						/>
					</Row>
				}
			>
				<PortfolioQueryWidgetBase query={investmentsMetricsQuery}>
					{({ allMetricTypes, rowData }) => (
						<ReactQueryWrapperBase query={conceptDefinitionsQuery}>
							{({ data: conceptDefinitions }) => (
								<PortfolioMetricsInner
									allMetricTypes={allMetricTypes}
									rowData={rowData}
									columnStates={columnStates}
									columns$={columns$}
									investmentsCount={investments.length}
									conceptDefinitions={conceptDefinitions}
									parametrizedConcepts={parametrizedConcepts}
									showDelta={showDelta}
								/>
							)}
						</ReactQueryWrapperBase>
					)}
				</PortfolioQueryWidgetBase>
			</Card>
		</MetricsBlurOverlay>
	);
}
